import React from "react";
import { CheckCircle2, Package, Factory, Truck, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import QuoteForm from "@/components/site/QuoteForm";

const BENEFITS = [
  {
    icon: Package,
    title: "Abastecimento contínuo",
    text: "Contratos de fornecimento recorrente para consumíveis de alto giro.",
  },
  {
    icon: Factory,
    title: "Especificação validada",
    text: "Itens compatíveis com equipamentos OEM e padrões industriais.",
  },
  {
    icon: Truck,
    title: "Logística sob controle",
    text: "Atendimento em todo o Brasil e LATAM com acompanhamento ativo.",
  },
  {
    icon: ShieldCheck,
    title: "Qualidade certificada",
    text: "Documentação, rastreabilidade e fornecedores auditados.",
  },
];

const CATEGORIES = [
  "Consumíveis de perfuração",
  "Químicos e vedantes industriais",
  "Abrasivos e materiais de desgaste",
  "Materiais para completação e produção",
  "Lubrificantes e fluidos especiais",
  "Acessórios de linha, superfície e montagem",
];

export default function Consumiveis() {
  return (
    <>
      <section className="bg-white border-b border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
              Linha dedicada
            </div>
            <h1 className="mt-4 font-display font-black text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <span className="text-brand">Materiais consumíveis</span>{" "}
              industriais com fornecimento confiável.
            </h1>
            <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
              Abastecemos operações de óleo e gás, energia e indústria pesada
              com materiais consumíveis críticos — de químicos e vedantes a
              insumos de perfuração e acessórios de linha. Foco em
              disponibilidade, qualidade e previsibilidade de preço.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#form"
                data-testid="consumiveis-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-7 py-4 rounded-sm hover:bg-brand-mid transition-colors border border-transparent hover:border-bronze"
              >
                Solicitar cotação
              </a>
              <a
                href="#categorias"
                className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-semibold px-7 py-4 rounded-sm hover:bg-surface-alt transition-colors"
              >
                Ver categorias
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-sm overflow-hidden border border-surface-line">
            <img
              src="https://images.unsplash.com/photo-1758873263428-f4b2edb45fe1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3BhcmUlMjBwYXJ0cyUyMHByZWNpc2lvbnxlbnwwfHx8fDE3NzY5NjQxODB8MA&ixlib=rb-4.1.0&q=85"
              alt="Componentes industriais e consumíveis"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section id="categorias" className="py-20 lg:py-24 bg-surface-alt border-y border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Categorias"
            title="O que fornecemos em consumíveis."
            subtitle="Se o item é de alto giro, crítico para disponibilidade ou exige especificação rigorosa — trabalhamos com ele."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((c) => (
              <div
                key={c}
                data-testid={`consumivel-cat-${c}`}
                className="p-6 bg-white border border-surface-line rounded-sm flex items-start gap-3 hover:border-bronze/40 transition-colors"
              >
                <CheckCircle2 className="h-5 w-5 text-brand mt-0.5" />
                <span className="text-sm font-medium text-ink">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Benefícios"
            title="Por que contratar consumíveis com a Wellpec."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-7 bg-white border border-surface-line rounded-sm hover:border-bronze/40 transition-colors"
              >
                <div className="h-11 w-11 rounded-sm bg-surface-alt text-brand flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <h4 className="mt-4 font-display font-bold text-ink">
                  {title}
                </h4>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="form" className="py-20 lg:py-24 bg-surface-alt border-t border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Orçamento"
              title="Cotação de consumíveis em até 24h."
              subtitle="Envie a especificação, quantidade e prazo. Nosso time retorna com proposta comercial e alternativas técnicas, quando aplicável."
            />
          </div>
          <div className="lg:col-span-7">
            <QuoteForm source="consumiveis" subjectDefault="Cotação — Materiais consumíveis" />
          </div>
        </div>
      </section>
    </>
  );
}
